import { Box, Button, Cell, Layout, Radio } from '@wix/design-system'
import { ChoiceQuestionContentComponent, useContent, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import React, { FC, useState } from 'react'
import { useMediaQuery } from 'react-responsive'

import { usePageLogger } from '../../../bi/mfi'
import { HEADLESS_MOBILE } from '../../../breakpoints'
import { useBrowserTabTitle } from '../../../hooks/useBrowserTabTitle'
import { ContentWithLoading } from '../../reusable/Button'
import { HeadlessTemplate } from '../../reusable/HeadlessTemplate'
import { dataHooks } from './HeadlessRadioInput.dataHooks'
import s from './HeadlessRadioInput.scss'

type Option = {
  label: string
}

export type Content = {
  backButton: string
  nextButton: string
  skipButton: string
  stepText: string
  browserTabTitle: string
  title: string
  subtitle: string
  cardTitle: string
  question: ChoiceQuestionContentComponent<{}, Option>
}

export const HeadlessRadioInput: FC = () => {
  const [selectedOptionKey, setSelectedOptionKey] = useState<string>()
  const [isPageLoading] = usePageLoading()
  const { content } = useContent<Content>()
  const { question } = content
  const { questionKey, options } = question
  const isMobileView = useMediaQuery(HEADLESS_MOBILE)

  useBrowserTabTitle(content.browserTabTitle)

  const next = useNext()

  const pageLogger = usePageLogger()

  const handleOptionClick = (optionKey: string) => {
    if (optionKey === selectedOptionKey) {
      return
    }
    void pageLogger.clickAnswer({ questionKey, buttonName: optionKey })
    setSelectedOptionKey(optionKey)
  }

  const handleSkipClick = () => {
    next({ [questionKey]: { skipped: true } })
  }

  const handleNextClick = () => {
    void pageLogger.answer({ answer: selectedOptionKey || '' })
    next({ [questionKey]: { values: selectedOptionKey ? [selectedOptionKey] : [] } })
  }

  const optionsNode = (
    <Box className={s.optionsNode}>
      <Layout gap="12px">
        {options.map((option, index) => (
          <Cell span={isMobileView ? 12 : 6} key={option.optionKey}>
            <div
              className={`${s.radio} ${option.optionKey === selectedOptionKey ? s.selected : ''}`}
              onClick={() => handleOptionClick(option.optionKey)}
            >
              <Radio
                checked={option.optionKey === selectedOptionKey}
                value={option.optionKey}
                label={option.label}
                dataHook={dataHooks.radio({ index })}
              />
            </div>
          </Cell>
        ))}
      </Layout>
    </Box>
  )

  return (
    <HeadlessTemplate
      isPageLoading={isPageLoading}
      pageLogger={pageLogger}
      stepText={content.stepText}
      title={content.title}
      subtitle={content.subtitle}
      cardTitle={content.cardTitle}
      backButton={content.backButton}
      skipButton={content.skipButton}
      onSkipClick={handleSkipClick}
      dataHook={dataHooks.template()}
    >
      {optionsNode}
      <Box className={s.button}>
        <Button
          size="large"
          onClick={handleNextClick}
          fullWidth
          disabled={!selectedOptionKey}
          dataHook={dataHooks.nextButton()}
        >
          <ContentWithLoading isLoading={isPageLoading}>{content.nextButton}</ContentWithLoading>
        </Button>
      </Box>
    </HeadlessTemplate>
  )
}
